import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useFlags } from "@/lib/flags";
import SEO from "@/components/site/SEO";
import { getSchoolUrl } from "@/lib/slug";
import { StarRating } from "@/components/site/Stars";
import { formatSppRange } from "@/lib/fees";
import { MapPin, Building2, ArrowRight, GraduationCap } from "lucide-react";

const fmtIDR = (n) => (typeof n === "number" && n > 0 ? `Rp ${Math.round(n / 1_000_000)} jt` : "—");

/** /campuses/:slug — Campus detail page listing all School Units under it. */
export default function CampusDetail() {
  const { school_reviews_enabled: reviewsOn } = useFlags();
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/campuses/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); });
  }, [slug]);

  if (notFound) return (
    <div className="max-w-2xl mx-auto py-20 px-4 text-center">
      <div className="font-serif-display text-3xl text-[#1F2937]">Campus tidak ditemukan</div>
      <button onClick={() => nav("/sekolah")} className="mt-4 text-[#D2673E] underline-offset-4 hover:underline">Kembali ke daftar sekolah →</button>
    </div>
  );
  if (!data) return <div className="p-10 text-center text-[#4B5563]">Memuat…</div>;

  const { campus, units, group } = data;

  return (
    <div data-testid="campus-detail-page" className="max-w-full sm:max-w-7xl sm:mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12 pb-32">
      <SEO title={`${campus.name} — The School Reports`} description={`${campus.name} — ${campus.unit_count || 0} unit sekolah${campus.city ? ` di ${campus.city}` : ""}. Lihat rating, biaya, dan review orang tua.`} />

      {/* Breadcrumb */}
      {group && (
        <div className="mb-3 text-xs text-[#4B5563]" data-testid="campus-breadcrumb">
          <Link to={`/grup/${group.slug}`} className="hover:text-[#D2673E]">{group.name}</Link>
          <span className="mx-2">›</span>
          <span>{campus.name}</span>
        </div>
      )}

      {/* Hero */}
      <div className="rounded-2xl bg-white border border-[#EAE7E0] p-6 sm:p-10 shadow-[0_4px_18px_-4px_rgba(31,41,55,0.08)]">
        <div className="flex items-start gap-6 flex-wrap">
          {campus.logo_url ? (
            <img src={campus.logo_url} alt={campus.name} className="w-24 h-24 rounded-2xl object-contain bg-[#F3F1ED] p-2 shrink-0" data-testid="campus-logo" />
          ) : (
            <div className="w-24 h-24 rounded-2xl bg-[#EFF6FF] text-[#1E3A8A] flex items-center justify-center shrink-0"><Building2 size={32} /></div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#D2673E] font-semibold">Campus</div>
            <h1 className="font-serif-display text-3xl sm:text-4xl text-[#1F2937] mt-1 leading-tight" data-testid="campus-name">{campus.name}</h1>
            {(campus.address_street || campus.city) && (
              <div className="mt-2 text-sm text-[#4B5563] inline-flex items-center gap-1" data-testid="campus-address">
                <MapPin size={13} /> {[campus.address_street, campus.kecamatan, campus.city, campus.state].filter(Boolean).join(", ")}
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-6 text-sm">
              <div><span className="text-[#4B5563]">Unit:</span> <strong className="text-[#1F2937]" data-testid="campus-unit-count">{campus.unit_count || 0}</strong></div>
              {reviewsOn && <div><span className="text-[#4B5563]">Rating rata-rata:</span> <span className="inline-flex items-center gap-1"><StarRating value={campus.avg_rating || 0} size={13} /><strong className="text-[#1F2937] ml-1">{(campus.avg_rating || 0).toFixed(1)}</strong></span></div>}
              {reviewsOn && <div><span className="text-[#4B5563]">Total review:</span> <strong className="text-[#1F2937]">{campus.review_count || 0}</strong></div>}
              <div><span className="text-[#4B5563]">Rentang SPP:</span> <strong className="text-[#1F2937]">{fmtIDR(campus.spp_min)}–{fmtIDR(campus.spp_max)}</strong></div>
            </div>
          </div>
        </div>
      </div>

      {/* Unit list */}
      <div className="mt-10">
        <h2 className="font-serif-display text-2xl text-[#1F2937] mb-4">Unit Sekolah</h2>
        {units.length === 0 ? (
          <div className="text-[#4B5563] italic">Belum ada unit terdaftar untuk campus ini.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="unit-grid">
            {units.map((u) => (
              <Link key={u.school_id} to={getSchoolUrl(u)} data-testid={`unit-card-${u.school_id}`}
                className="group bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden hover:border-[#D2673E] hover:-translate-y-0.5 transition-all">
                <div className="aspect-[4/3] bg-gradient-to-br from-[#DBE1EE] to-[#B8C5DB] overflow-hidden">
                  {u.image_url && <img src={u.image_url} alt={u.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
                </div>
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-widest text-[#D2673E] font-semibold inline-flex items-center gap-1"><GraduationCap size={11} /> {(u.education_levels || []).join(" · ") || "—"}</div>
                  <div className="font-serif-display text-lg text-[#1F2937] mt-1 leading-tight group-hover:text-[#D2673E] transition-colors line-clamp-2">{u.name}</div>
                  {reviewsOn && <div className="mt-3 flex items-center gap-2">
                    <StarRating value={u.avg_rating || 0} size={13} />
                    <span className="text-xs text-[#4B5563]">{u.review_count || 0} review</span>
                  </div>}
                  <div className="mt-3 pt-3 border-t border-[#EAE7E0] flex items-center justify-between text-xs">
                    <span className="text-[#4B5563] font-medium">{formatSppRange(u.spp_min, u.spp_max)}</span>
                    <span className="text-[#D2673E] font-semibold inline-flex items-center gap-1">Lihat <ArrowRight size={11} /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
